import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, ArrowRight, Server } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { useCheckout, fetchCatalog } from "../lib/useCheckout";

const setMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const formatPrice = (cents, currency) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() }).format(
    (cents || 0) / 100
  );

const HostingCard = () => (
  <div
    className="relative rounded-md p-8 md:rotate-[0.6deg]"
    style={{ border: "2px dashed rgba(230,237,247,0.3)" }}
    data-testid="pricing-card-hosting"
  >
    <Server size={22} className="text-[var(--ggm-signal-cyan)]" />
    <h3 className="mt-3 font-condensed uppercase text-white" style={{ fontSize: "clamp(1.25rem, 2.6vw, 1.75rem)", letterSpacing: "-0.01em" }}>
      AI Front Desk Hosting
    </h3>
    <p className="mt-3 font-body text-white/70 text-sm md:text-base leading-relaxed">
      Basic app hosting and basic maintenance for your AI Front Desk. Free for the first year after launch, then billed
      monthly. Cancel anytime. You still own the chatbot.
    </p>
    <div className="mt-6 flex items-baseline gap-2 flex-wrap">
      <span className="font-condensed text-white" style={{ fontSize: "clamp(2rem, 5vw, 3rem)", letterSpacing: "-0.02em" }} data-testid="pricing-amount-hosting">
        $75
      </span>
      <span className="font-body text-white/50 text-sm">per month · first year free</span>
    </div>
    <p className="mt-7 font-body text-white/60 text-sm">
      No need to sign up here. After your Front Desk goes live, you'll get a private link by email to save a card,
      with no charge until your free year ends.
    </p>
  </div>
);

export default function PricingPage() {
  const [catalog, setCatalog] = useState({ items: [], tax_mode: "" });
  const [loadErr, setLoadErr] = useState(null);
  const [qty, setQty] = useState({});
  const { startCheckout, loading, error } = useCheckout();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Pricing — AI Front Desk, Build Blueprint & Coaching · Gadget Guy Media";
    setMeta(
      "name",
      "description",
      "Fixed, published prices from Gadget Guy Media: AI Front Desk website chatbot $1,500 one time (hosting free for year one, then $75/mo), Build Blueprint $297, and AI coaching $75/hour. Pay securely with Stripe."
    );
    setLink("canonical", "https://gadgetguymedia.com/pricing");
    // Branded social card so LinkedIn/X previews show "$75 AI Coaching"
    // instead of the generic site OG image.
    const OG = "https://gadgetguymedia.com/brand-v2/favicon/og-pricing.png";
    setMeta("property", "og:image", OG);
    setMeta("name", "twitter:image", OG);
    fetchCatalog()
      .then(setCatalog)
      .catch((e) => setLoadErr(e.message || "Could not load pricing"));
  }, []);

  const items = catalog.items || [];

  return (
    <PageShell testId="pricing-page">
      <section
        className="relative"
        style={{ paddingTop: "clamp(48px, 8vh, 96px)", paddingBottom: "clamp(48px, 8vh, 96px)" }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-6" data-testid="pricing-eyebrow">
            Fixed Pricing · Pay Online
          </div>
          <h1
            className="font-condensed uppercase text-white leading-[1.02]"
            style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", letterSpacing: "-0.015em" }}
            data-testid="pricing-h1"
          >
            Fixed prices. Pay online.
          </h1>
          <p
            className="mt-5 font-body text-white/75 text-base md:text-lg leading-relaxed max-w-3xl"
            data-testid="pricing-subhead"
          >
            <span className="text-white font-bold">The first 30 minutes are free</span> — one or the other, your call:
            {" "}
            <Link to="/#start-a-project" className="underline text-white hover:text-[var(--ggm-signal-cyan)]">
              a virtual scoping call
            </Link>
            , <span className="text-white">or</span> an{" "}
            <Link to="/#start-a-project" className="underline text-white hover:text-[var(--ggm-signal-cyan)]">
              in-person Local AI Audit at your business
            </Link>
            {" "}(within 15 miles — I'll come to you). No obligation. Every price below is fixed and published, and payment is processed securely by Stripe.
          </p>

          {loadErr && (
            <div
              className="mt-8 p-4 rounded-md border border-red-500/40 text-red-200"
              data-testid="pricing-load-error"
            >
              {loadErr}
            </div>
          )}

          <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="pricing-grid">
            {items.map((item) => {
              const q = qty[item.lookup_key] || 1;
              return (
                <div
                  key={item.lookup_key}
                  className="relative rounded-md p-8"
                  style={{
                    background: "var(--ggm-surface)",
                    border: "2px solid rgba(40,200,240,0.55)",
                    boxShadow: "8px 8px 0 var(--ggm-orbit-blue)",
                  }}
                  data-testid={`pricing-card-${item.lookup_key}`}
                >
                  <h3
                    className="font-condensed uppercase text-white"
                    style={{ fontSize: "clamp(1.25rem, 2.6vw, 1.75rem)", letterSpacing: "-0.01em" }}
                  >
                    {item.name}
                  </h3>
                  <p className="mt-3 font-body text-white/70 text-sm md:text-base leading-relaxed">
                    {item.description}
                  </p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span
                      className="font-condensed text-white"
                      style={{ fontSize: "clamp(2rem, 5vw, 3rem)", letterSpacing: "-0.02em" }}
                      data-testid={`pricing-amount-${item.lookup_key}`}
                    >
                      {formatPrice(item.unit_amount, item.currency)}
                    </span>
                    <span className="font-body text-white/50 text-sm" data-testid={`pricing-note-${item.lookup_key}`}>{item.price_note}</span>
                  </div>

                  {item.allow_quantity && (
                    <div className="mt-6 flex items-center gap-3">
                      <label
                        htmlFor={`qty-${item.lookup_key}`}
                        className="font-condensed uppercase text-white/70 tracking-widest text-xs"
                      >
                        Hours
                      </label>
                      <input
                        id={`qty-${item.lookup_key}`}
                        type="number"
                        min={1}
                        max={20}
                        value={q}
                        onChange={(e) =>
                          setQty((prev) => ({
                            ...prev,
                            [item.lookup_key]: Math.max(1, Math.min(20, Number(e.target.value) || 1)),
                          }))
                        }
                        className="w-20 rounded-md px-3 py-2 text-white bg-black/30 border border-white/15 focus:outline-none focus:border-[var(--ggm-signal-cyan)]"
                        data-testid={`pricing-qty-${item.lookup_key}`}
                      />
                      <span className="font-body text-white/60 text-sm">
                        Subtotal: <b>{formatPrice((item.unit_amount || 0) * q, item.currency)}</b>
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => startCheckout({ lookup_key: item.lookup_key, quantity: q })}
                    disabled={loading}
                    className="ggm-btn-primary mt-7 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`pricing-buy-${item.lookup_key}`}
                  >
                    {loading ? "Redirecting…" : item.cta || "Buy"} <ArrowUpRight size={16} />
                  </button>

                  {item.learn_more && (
                    <Link to={item.learn_more} className="ml-4 font-body text-sm underline text-white/70 hover:text-[var(--ggm-signal-cyan)]" data-testid={`pricing-learn-${item.lookup_key}`}>
                      What's included
                    </Link>
                  )}

                  <div className="mt-5 flex items-center gap-2 text-white/50 text-xs">
                    <ShieldCheck size={14} className="text-[var(--ggm-signal-cyan)]" />
                    Secure payment via Stripe · card details never touch our servers
                  </div>
                </div>
              );
            })}

            {items.length > 0 && <HostingCard />}

            {items.length === 0 && !loadErr && (
              <div className="col-span-full font-body text-white/60" data-testid="pricing-empty">
                Loading pricing…
              </div>
            )}
          </div>

          {error && (
            <div
              className="mt-8 p-4 rounded-md border border-red-500/40 text-red-200"
              data-testid="pricing-checkout-error"
            >
              {error}
            </div>
          )}

          {/* FAQ — placed under the coaching card for richer SEO snippets */}
          <section
            className="mt-16 max-w-3xl"
            aria-labelledby="pricing-faq-heading"
            data-testid="pricing-faq"
          >
            <div className="ggm-eyebrow mb-4">Questions, answered</div>
            <h2
              id="pricing-faq-heading"
              className="font-condensed uppercase text-white leading-[1.05]"
              style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", letterSpacing: "-0.015em" }}
            >
              Before you book.
            </h2>
            <dl className="mt-8 space-y-6">
              <div data-testid="pricing-faq-item-included">
                <dt
                  className="font-condensed uppercase text-white"
                  style={{ fontSize: "1.1rem", letterSpacing: "-0.005em" }}
                >
                  What do I actually get in a coaching hour?
                </dt>
                <dd className="mt-2 font-body text-white/75 text-base leading-relaxed">
                  A 1-on-1 Zoom working session with me. We screen-share, dig into
                  the real workflow or build you're stuck on, and leave with
                  prompts, tools, and a working next step — not a slide deck.
                  Great for prompting, vibe coding, debugging an AI build, or
                  scoping your first automation.
                </dd>
              </div>
              <div data-testid="pricing-faq-item-booking">
                <dt
                  className="font-condensed uppercase text-white"
                  style={{ fontSize: "1.1rem", letterSpacing: "-0.005em" }}
                >
                  How does scheduling work after I pay?
                </dt>
                <dd className="mt-2 font-body text-white/75 text-base leading-relaxed">
                  Stripe emails your receipt the moment payment clears, and I
                  reach out personally within 1 business day with a short list of
                  available times. Buying multiple hours at once is fine — we can
                  split them across sessions.
                </dd>
              </div>
              <div data-testid="pricing-faq-item-refund">
                <dt
                  className="font-condensed uppercase text-white"
                  style={{ fontSize: "1.1rem", letterSpacing: "-0.005em" }}
                >
                  What if I need to cancel or want a refund?
                </dt>
                <dd className="mt-2 font-body text-white/75 text-base leading-relaxed">
                  Reschedule any unused hour at no cost. If you decide coaching
                  isn't right for you before we meet, email{" "}
                  <a
                    href="mailto:info@gadgetguymedia.com"
                    className="underline text-white hover:text-[var(--ggm-signal-cyan)]"
                  >
                    info@gadgetguymedia.com
                  </a>{" "}
                  for a full refund on any unused hours. Receipts and card
                  updates live in the{" "}
                  <Link
                    to="/billing"
                    className="underline text-white hover:text-[var(--ggm-signal-cyan)]"
                  >
                    billing portal
                  </Link>
                  .
                </dd>
              </div>
            </dl>
          </section>

          <div className="mt-14 flex flex-wrap gap-3 items-center">
            <Link to="/#start-a-project" className="ggm-btn-secondary" data-testid="pricing-cta-project">
              Have a build in mind instead? <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
